import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { getCurrentTenant } from '../../states/tenant-state/tenant.selectors';
import { getIsCurrentUserAdmin } from '../../states/user-state/user.selectors';
import { TenantState } from '../../states/tenant-state/tenant.state';
import { marker as _ } from '@bling-fe/shared/utils';
import { IonMenu } from '@ionic/angular';
import { select, Store } from '@ngrx/store';
import {
  getCommunityNotificationCount,
  getServicesNotificationCount
} from '../../states/message-notifications-state/message-notifications.selectors';
import { map, Observable, take } from 'rxjs';
import { ElementOrderRepository } from '../../../shared/repositories/element-order.repository';
import { orderByKeys } from '../../../shared/utils/order-by-keys';

export interface MenuSection {
  label: string;
  items: MenuItem[];
}

export interface MenuItem {
  label: string;
  url: string;
  icon: string;
  class?: string;
  showArrowIcon?: boolean;
  separator?: boolean;
  notificationCount$?: Observable<number>;
}

@Component({
  selector: 'fl-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  standalone: false
})
export class MainMenuComponent {
  @ViewChild(IonMenu) menu: IonMenu;

  communityNotifications$ = this.store.select(getCommunityNotificationCount);
  serviceNotifications$ = this.store.select(getServicesNotificationCount);

  private readonly defaultMenuItems: MenuItem[] = [
    {
      label: _('PAGES.TABS.PINNWAND'),
      url: '/home',
      icon: 'push-pin'
    },
    {
      label: _('PAGES.TABS.CALENDAR'),
      url: '/calendar',
      icon: 'calendar'
    },
    {
      label: _('PAGES.TABS.COMMUNITY'),
      url: '/community',
      icon: '3-user',
      notificationCount$: this.communityNotifications$
    },
    {
      label: _('PAGES.TABS.MY_TENENT'),
      url: '/my-tenant',
      icon: 'suitcase-simple',
      notificationCount$: this.serviceNotifications$
    },
    {
      label: _('PAGES.TABS.PROFILE'),
      url: '/my-profile',
      icon: 'profile'
    }
  ];

  currentTenant$ = this.store.pipe(select(getCurrentTenant));
  isAdmin$ = this.store.select(getIsCurrentUserAdmin);

  // Items ordered by the tenant's saved nav order (falls back to the default).
  // `id` (= url) is the reorder key the reorderable list emits.
  desktopMenuItems$: Observable<Array<MenuItem & { id: string }>> =
    this.currentTenant$.pipe(
      map(tenant =>
        orderByKeys(
          this.defaultMenuItems,
          item => item.url,
          tenant && tenant !== 'loading'
            ? tenant.tenant_setting?.element_order?.main_nav
            : undefined
        ).map(item => ({ ...item, id: item.url }))
      )
    );

  mobileMenuItems$: Observable<MenuSection[]> = this.desktopMenuItems$.pipe(
    map(items => [{ label: '', items }])
  );

  constructor(
    private router: Router,
    private store: Store<TenantState>,
    private elementOrderRepository: ElementOrderRepository
  ) {}

  navigateUrl(url: string) {
    this.router.navigateByUrl(url);
  }

  saveNavOrder(order: Array<string | number>): void {
    this.elementOrderRepository
      .updateOrder('main_nav', order)
      .pipe(take(1))
      .subscribe();
  }
}
